<script lang="ts">
	import { readable } from 'svelte/store';
	import { twMerge } from 'tailwind-merge';
	import type {
		CartItem,
		CartItems,
	} from '#lib/features/checkout/utils/createCartItemsStore/index.js';
	import { WishlistMixpanelEvents } from '#lib/features/wishlist/constants/mixpanel-events.js';
	import { getWishlistContext } from '#lib/features/wishlist/utils/getWishlistContext/index.js';
	import { trackWishlistEvent } from '#lib/features/wishlist/utils/trackWishlistEvent/index.js';
	import { PrimaryMixpanelEvents } from '#lib/websites/primary/constants/mixpanel-events.js';
	import { HeartIcon } from '$global/assets/icons/HeartIcon';

	interface Props {
		productId: string | null | undefined;
		cartItem: Omit<CartItem, 'quantity'>;
		artist: string | null | undefined;
		trackPrefix: string;
		dataCy: string;
		class?: string;
	}

	let { productId, cartItem, artist, trackPrefix, dataCy, ...rest }: Props =
		$props();

	const wishlistCtx = getWishlistContext();
	// Fall back to an inert store when the context is absent (flag off), so the
	// `$wishlistItems` auto-subscription never runs against `undefined`.
	const wishlistItems =
		wishlistCtx?.wishlistItems.cartItems ?? readable({} as CartItems);

	let isInWishlist = $derived(!!productId && !!$wishlistItems[productId]);

	const toggle = (event: Event) => {
		// Used as an overlay on top of clickable cards/links; stop the click from
		// bubbling to the surrounding anchor.
		event.preventDefault();
		event.stopPropagation();

		if (!wishlistCtx || !productId) {
			return;
		}

		if (isInWishlist) {
			wishlistCtx.wishlistItems.removeFromCart(
				productId,
				PrimaryMixpanelEvents
			);

			trackWishlistEvent(
				trackPrefix,
				WishlistMixpanelEvents.RemoveFromWishlist,
				{
					artworkId: cartItem.entityId,
					artist,
				}
			);
		} else {
			wishlistCtx.wishlistItems.addToCart(
				productId,
				cartItem,
				1,
				PrimaryMixpanelEvents
			);

			trackWishlistEvent(trackPrefix, WishlistMixpanelEvents.AddToWishlist, {
				artworkId: cartItem.entityId,
				artist,
			});
		}
	};
</script>

{#if wishlistCtx}
	<button
		type="button"
		onclick={toggle}
		aria-pressed={isInWishlist}
		aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
		data-cy={`${dataCy}-wishlist-toggle`}
		class={twMerge(
			'flex items-center justify-center rounded-full transition-colors outline-none',
			rest.class
		)}
	>
		<HeartIcon full={isInWishlist} />
	</button>
{/if}
